import { FunctionComponent, ReactElement } from "react";
import LayoutWrapper from "../../shared/layout";
import {
	Accordion,
	AccordionButton,
	AccordionIcon,
	AccordionItem,
	AccordionPanel,
	Box,
	Heading,
	ListItem,
	Text,
	UnorderedList
} from "@chakra-ui/react";
import { Page } from "../../shared/layout/index.styles";

interface ReleaseNotesPageProps {}

const ReleaseNotesPage: FunctionComponent<ReleaseNotesPageProps> = (props: ReleaseNotesPageProps): ReactElement => {
	// Hardcoded release notes
	const releaseNotes = [
		{
			date: "August 2026",
			version: "1.18.0",
			description: [
				{
					header: "High Value Flat Fee Services",
					body: "High value flat fee services on the case detail page can now be expanded to show the specific work levels selected for that service. Standard services continue to display as a simple list, so only services with extra detail take up the additional space."
				},
				{
					header: "Claim ID on Payments",
					body: "The Payments tab on a claim now includes a Claim ID column, so it is clear at a glance which claim each bill belongs to. Bills with no claim recorded against them display as Not Specified."
				},
				{
					header: "Billable Codes When Adding Rates",
					body: "When adding a rate to a provider profile, the billing code dropdown now only offers codes that are currently billable. Rates already saved against a code that is no longer billable are not affected and still display their full name."
				},
				{
					header: "Simplified Claim Statuses",
					body: "Claims closed as goodwill now display to attorneys as Closed - Not Paid, rather than showing the internal goodwill wording. Filtering by that status returns both kinds together, so nothing is hidden from the list."
				},
				{
					header: "Flat Fee and Hourly Billing Conversion",
					body: "During claim submission, a benefit set up as high fixed value can be billed hourly, and an hourly benefit can be billed as a flat amount. The hours field appears automatically when converting to hourly. This went live in an earlier deployment and was not previously documented."
				},
				{
					header: "Unexpected Sign-Outs",
					body: "Attorneys are no longer signed out when they attempt an action they do not have permission for. Previously a permission error was treated as a sign-in problem, which ended the session and forced a fresh login. The action is now simply declined and the session continues."
				}
			]
		},
		{
			date: "May 2026",
			version: "1.17.0 - 1.17.1",
			description: [
				{
					header: "Corrected Received On Dates",
					body: "Attorney-created claims now have received on dates populated more often."
				},
				{
					header: "Added Release Notes Page",
					body: "Users can now view release notes."
				},
				{
					header: "Removed Duplicate and Non-Billable Benefits from List",
					body: "Duplicate and non-billable benefits have now been removed from benefit selection lists, like when entering standard network rates or selecting benefits for a claim."
				}
			]
		},
		{
			date: "April 2026",
			version: "1.15.0 - 1.16.0",
			description: [
				{
					header: "Default Standard Rates Added",
					body: "Attorneys can now enter default standard rates that will be set on future new claims."
				},
				{
					header: "Identifying Info Added to Claim Status Update Emails",
					body: "Attorneys now see identifying data in claim status update emails to more easily find what case it is for."
				},
				{
					header: "Password Reset Email Link Fixed",
					body: "The password reset link emailed to users was fixed so that it would correctly work for emails with special characters."
				},
				{
					header: "Table Loading Improved",
					body: "Table loading UI was updated to not block the entire page and instead allow navigation."
				},
				{
					header: "Request for Claim Information Email Added",
					body: "A new email was added specifically for requesting specific documents from attorneys."
				}
			]
		},
		{
			date: "March 2026",
			version: "1.13.0 - 1.14.0",
			description: [
				{
					header: "Help Banner Added",
					body: "A help banner on the Cases page now displays who to contact in case an attorney does not see specific cases in their list."
				},
				{
					header: "Claim Intake Steps Reordered",
					body: "The steps in Claim Intake were reordered so that the Documents step comes before the Billing step."
				},
				{
					header: "Claim Payment Check Numbers Displayed",
					body: "Check numbers were added for attorneys to view in the Payments tab of a Claim."
				}
			]
		},
		{
			date: "February 2026",
			version: "1.11.0 - 1.12.0",
			description: [
				{
					header: "Password Requirements Updated",
					body: "Displayed password requirements were updated on the password reset page to avoid confusion."
				},
				{
					header: "Cases Table Member ID Column Added",
					body: "Member IDs were added as a column to attorney's list of cases for easier searching."
				},
				{
					header: "Claims Table Searching & Sorting Added",
					body: "Searching and sorting was added to the Member Name column of the attorney Claims table for easier searching."
				},
				{
					header: "Claims Table Amount Corrected",
					body: "The amount shown in the attorney Claims table was corrected to be the Total Network Cost instead of the Market Value Total for each claim."
				}
			]
		},
		{
			date: "January 2026",
			version: "1.8.0 - 1.10.0",
			description: [
				{
					header: "Update Prompt Added",
					body: "A UI prompt was added so that after deployments, users will be asked to refresh their page to see the updated UI."
				},
				{
					header: "Claims Table Columns Added",
					body: "The LAMP Case ID and Attorney ID columns were added to the attorney Claims table for easier searching."
				},
				{
					header: "Automatic PDF Claim Form Generation Added",
					body: "Automatic generation of pdf Claim Forms was added during claim intake, avoiding the need for attorneys to upload their own claim forms."
				},
				{
					header: "Email Notification Preferences Added",
					body: "A page for updating email notification preferences was added so attorneys can customize which update emails they receive."
				}
			]
		},
		{
			date: "December 2025",
			version: "1.7.0",
			description: [
				{
					header: "Service Recipient Improved",
					body: "When adding multiple services to a claim, the last-used recipient will be auto filled for new services to help improve efficiency."
				},
				{
					header: "LAMP Case Fetching Improved",
					body: "Fetching LAMP Cases was improved so that the cases can be fetched faster."
				}
			]
		},
		{
			date: "November 2025",
			version: "1.5.0 - 1.6.3",
			description: [
				{
					header: "Service Recipient Improved",
					body: "When adding multiple services to a claim, the last-used recipient will be auto filled for new services to help improve efficiency."
				},
				{
					header: "Member Policy Cancellation Date Corrected",
					body: "The member policy cancellation date was corrected so that the last day of a plan can be included in a claim."
				},
				{
					header: "Claim Filing State Field Added",
					body: "Filing state was added as a field to claims, allowing attorneys to know where the claim is being filed."
				},
				{
					header: "Login Navigation Corrected",
					body: "Issues in navigating after login were corrected."
				}
			]
		},
		{
			date: "October 2025",
			version: "1.0.0 - 1.4.6",
			description: [
				{
					header: "Maximum Document Size Added",
					body: "A maximum document size was added, as well as a total documents size allowed on a claim."
				},
				{
					header: "Claim Received On Date Added",
					body: "The Received On date field was added to claims to track when claims were received."
				},
				{
					header: "Increased Hourly Rates Added",
					body: "Increased hourly rates are now synchronized and fetched from LAMP."
				},
				{
					header: "Claim Intake Dates Corrected",
					body: "The initial consultation and final service dates in claim intake were corrected."
				},
				{
					header: "Billing Values Rounded",
					body: "Rounding was added to billing values in claim intake."
				},
				{
					header: "Identical Service & Recipient Allowed",
					body: "Constraints were changed so that identical services and recipients can be selected during claim intake."
				},
				{
					header: "Hourly to Flat Billing Conversion Added",
					body: "The ability to convert hourly billing to flat billing was added to the Billing step of claim intake."
				}
			]
		}
	];

	return (
		<>
			<LayoutWrapper>
				<Box width="100%" height="100%">
					<Box mx={8}>
						<Page>
							<Heading size="lg" mb={"5"} textAlign="center" color="secondary">
								Release Notes
							</Heading>
							{/* Release Notes Accordion */}
							<Accordion allowToggle mb={5} defaultIndex={0}>
								{releaseNotes.map((note, index) => (
									<AccordionItem key={index}>
										<AccordionButton>
											<Box flex="1" textAlign="left">
												<Heading minW="180px" size="md" mb={3} display="inline-block">
													{note.date}
												</Heading>
												{/* Display version as a heading */}
												<Text
													size="sm"
													fontStyle="italic"
													fontWeight="500"
													mb={3}
													color="orange.600"
													display="inline-block">
													Version {note.version}
												</Text>
											</Box>
											<AccordionIcon />
										</AccordionButton>
										<AccordionPanel pb={4} ml={4}>
											{/* Display description */}
											{typeof note.description === "string" ? (
												<Text>{note.description}</Text>
											) : (
												<UnorderedList variant="orange">
													{note.description.map((desc, idx) => (
														<ListItem key={idx} mb={4}>
															<Text fontWeight="semibold" mb={2}>
																{desc.header}
															</Text>
															<Text ml={3}>{desc.body}</Text>
														</ListItem>
													))}
												</UnorderedList>
											)}
										</AccordionPanel>
									</AccordionItem>
								))}
							</Accordion>
						</Page>
					</Box>
				</Box>
			</LayoutWrapper>
		</>
	);
};

export default ReleaseNotesPage;
